import type { DataTableDataSearchDto } from '@asset-metrix/portal-gateway-client-js';
import { EMPTY_VALUE } from '@asset-metrix/portal-ui';

import type { FilterSelectionValue } from '../../store/states/filterSelection';
import {
  type FilterConfigDto,
  type FilterData,
  isDateRangePickerDisplay,
} from '../../store/states/filterTypes';
import { findOptionLabel } from '../../utils/comparisonDetailPageNav';
import { formatPageFilterUrlValuesForDisplay } from '../filter/formatter';

export type DashboardFilterSelections = NonNullable<
  DataTableDataSearchDto['selection']
>;

function hasFilterSelections(
  selections: DashboardFilterSelections | undefined
): selections is DashboardFilterSelections {
  return selections != null && Object.keys(selections).length > 0;
}

export function storedFilterSelections(tile: {
  pageFilterSelections?: DashboardFilterSelections;
  customTableFilterSelections?: DashboardFilterSelections;
}): {
  pageFilterSelections?: DashboardFilterSelections;
  customTableFilterSelections?: DashboardFilterSelections;
} {
  return {
    ...(hasFilterSelections(tile.pageFilterSelections)
      ? { pageFilterSelections: tile.pageFilterSelections }
      : {}),
    ...(hasFilterSelections(tile.customTableFilterSelections)
      ? { customTableFilterSelections: tile.customTableFilterSelections }
      : {}),
  };
}

function formatSelectionValue(
  value: string | number | boolean,
  config: FilterConfigDto | undefined,
  filterData: FilterData | undefined
): string {
  const raw = String(value);
  return (
    findOptionLabel(filterData, raw) ??
    (formatPageFilterUrlValuesForDisplay([raw], config) || raw)
  );
}

export function formatFilterSelectionValues(
  value: FilterSelectionValue | undefined,
  config?: FilterConfigDto,
  filterData?: FilterData
): string[] {
  if (value === undefined) {
    return [];
  }

  if (value === null) {
    return [EMPTY_VALUE];
  }

  if (typeof value === 'object' && !Array.isArray(value)) {
    const parts = [value.start, value.end].filter(
      (part): part is string | number => part != null && part !== ''
    );
    if (parts.length === 0) {
      return [];
    }
    return [
      parts
        .map((part) => formatSelectionValue(part, config, filterData))
        .join(' – '),
    ];
  }

  const values = Array.isArray(value) ? value : [value];
  return values.flatMap((entry) => {
    if (entry == null) {
      return [EMPTY_VALUE];
    }
    const formatted = formatSelectionValue(entry, config, filterData);
    // An empty string is a selected option (the blank choice). Show it the
    // same way the select does when that option has no label.
    return [formatted === '' ? EMPTY_VALUE : formatted];
  });
}

export type DashboardFilterPill = {
  key: string;
  label: string;
  values: string[];
};

function isFreeformFilterDisplay(display: FilterConfigDto['display']): boolean {
  return (
    display === 'search' ||
    display === 'range-number-picker' ||
    isDateRangePickerDisplay(display)
  );
}

export function buildDashboardFilterPills(
  selections: DashboardFilterSelections | undefined,
  configs: readonly FilterConfigDto[],
  filterDataByKey: Readonly<Record<string, FilterData | undefined>> = {}
): DashboardFilterPill[] {
  const configByKey = new Map(configs.map((config) => [config.key, config]));
  const keys = [
    ...Object.keys(selections ?? {}),
    ...configs
      .map((config) => config.key)
      .filter((key) => selections == null || !(key in selections)),
  ];
  const pills: DashboardFilterPill[] = [];

  for (const key of keys) {
    const config = configByKey.get(key);
    const filterData = filterDataByKey[key];
    // Option pills use the selection returned with the option payload. That
    // value is already limited to options the current user view can see.
    // Search and range filters have no option list, so once their request has
    // settled they keep the saved selection. The tile shows a spinner while
    // the request is loading and the page-filter error when it fails.
    const value =
      config != null && isFreeformFilterDisplay(config.display)
        ? selections?.[key]
        : filterData?.selection;
    const values = formatFilterSelectionValues(value, config, filterData);
    if (values.length === 0) {
      continue;
    }
    pills.push({
      key,
      label: config?.label || key,
      values,
    });
  }

  return pills;
}
